import React, { useMemo, useState } from 'react';
import { FormikProps } from 'formik';
import { Col, Row } from 'antd';
import { Input } from 'formik-antd';
import { useDidUpdate } from 'rooks';

import { useAppSelector } from 'hooks/useAppSelector';
import {
  countriesSelector,
  isStatesLoadingSelector,
  statesSelector,
  citiesSelector,
  isCitiesLoadingSelector,
  isCountriesLoadingSelector,
} from 'redux/selectors/regionSelectors';
import FormItem from 'components/formik/FormItem';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import { WorkerInfoFormValues } from '../WorkerSection';

type Props<T> = {
  formik: FormikProps<T>;
  isAddWorkerOptionSelected: boolean;
  handleWorkerAddressChange: (value?: string) => void;
};

export default function WorkerStateForm<T extends WorkerInfoFormValues>(
  props: Props<T>,
) {
  const { formik, isAddWorkerOptionSelected, handleWorkerAddressChange } =
    props;
  const [selectedState, setSelectedState] = useState(
    formik.values.worker.state,
  );
  const [selectedCountry, setSelectedCountry] = useState(
    formik.values.worker.country,
  );
  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const isCitiesLoading = useAppSelector(isCitiesLoadingSelector);
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const cities = useAppSelector(citiesSelector);

  useDidUpdate(() => {
    setSelectedCountry(formik.values.worker.country);
  }, [formik.values.worker.country]);

  const filteredStates = useMemo(
    () => states.filter((state) => state.countryId === selectedCountry),
    [selectedCountry, states],
  );

  const filteredCities = useMemo(
    () => cities.filter((city) => city.stateId === selectedState),
    [selectedState, cities],
  );

  const handleCountryChange = (value: number, formik: FormikProps<T>) => {
    setSelectedCountry(value);

    formik.setFieldValue('worker.city', '', false);
    formik.setFieldValue('worker.state', '', false);
    formik.setFieldValue('worker.internationalCityName', '', false);
    formik.setFieldValue('worker.internationalStateName', '', false);
  };

  const handleStateChange = (stateId: number, formik: FormikProps<T>) => {
    setSelectedState(stateId);

    formik.setFieldValue('worker.city', '', false);
  };

  return (
    <Row gutter={40}>
      <Col xs={24} md={12}>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <FormItem
              name="worker.country"
              label="Country *"
              data-id="newAssignmentWorkerCountryField"
            >
              <FormikSelectWithLoading
                isLoading={isCountriesLoading}
                disabled={!isAddWorkerOptionSelected}
                name="worker.country"
                showSearch
                placeholder="Select country"
                optionFilterProp="children"
                onChange={(value: number) => handleCountryChange(value, formik)}
              >
                {countries.map((country) => (
                  <FormikSelectWithLoading.Option
                    key={`select-country-${country.id}`}
                    value={country.id || -1}
                  >
                    {country.name}
                  </FormikSelectWithLoading.Option>
                ))}
              </FormikSelectWithLoading>
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem
              name="worker.state"
              label="State *"
              data-id="newAssignmentWorkerStateField"
            >
              <FormikSelectWithLoading
                isLoading={isStatesLoading}
                disabled={!isAddWorkerOptionSelected}
                name="worker.state"
                showSearch
                placeholder="Select state"
                optionFilterProp="children"
                onChange={(value: number) => handleStateChange(value, formik)}
              >
                {filteredStates.map((state) => (
                  <FormikSelectWithLoading.Option
                    key={`select-state-${state.id}`}
                    value={state.id || -1}
                  >
                    {state.name}
                  </FormikSelectWithLoading.Option>
                ))}
              </FormikSelectWithLoading>
            </FormItem>
          </Col>
        </Row>
      </Col>

      <Col xs={24} md={12}>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <FormItem
              name="worker.city"
              label="City *"
              data-id="newAssignmentWorkerCityField"
            >
              <FormikAutoCompleteWithLoading
                isLoading={isCitiesLoading}
                name="worker.city"
                filterOption
                disabled={
                  !formik.values.worker.state || !isAddWorkerOptionSelected
                }
                placeholder="Enter city"
                showSearch
              >
                {filteredCities.map((city) => (
                  <FormikAutoCompleteWithLoading.Option
                    key={`worker-city-${city.id}`}
                    value={city.name || ''}
                  >
                    {city.name}
                  </FormikAutoCompleteWithLoading.Option>
                ))}
              </FormikAutoCompleteWithLoading>
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem
              label="Address *"
              data-id="newAssignmentWorkerAddressField"
              name="worker.address"
            >
              <Input
                disabled={!isAddWorkerOptionSelected}
                name="worker.address"
                placeholder="Enter address"
                autoComplete="off"
                onChange={(e) => handleWorkerAddressChange(e.target.value)}
                suffix={<span />}
              />
            </FormItem>
          </Col>
        </Row>
      </Col>
      <Col xs={24} md={12}>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <FormItem
              label="ZIP"
              data-id="newAssignmentWorkerZipField"
              name="worker.zip"
            >
              <Input
                disabled={!isAddWorkerOptionSelected}
                name="worker.zip"
                placeholder="Enter ZIP code"
                autoComplete="off"
              />
            </FormItem>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}
